import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';

@Component({
  selector: 'app-accordion-content',
  templateUrl: './accordion-content.component.html',
  styleUrls: ['./accordion-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccordionContentComponent implements AfterViewInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  transitionDuration = 200;
  // used to save performance with *ngIf.
  // This allows the parent to hide the ng-content when none of it is visible and still allows the animation to work.
  private _contentCompletelyHidden$ = new BehaviorSubject(true);
  contentCompletelyHidden$ = this._contentCompletelyHidden$.pipe(
    rxNgZoneRun(this.ngZone),
  );

  gridTemplateRows$ = new BehaviorSubject<string>('0fr');
  @HostBinding('style.grid-template-rows') get gridTemplateRows() {
    return this.gridTemplateRows$.value;
  }

  overflow$ = new BehaviorSubject<string>('hidden');

  transition$ = new BehaviorSubject<string>('');
  @HostBinding('style.transition') get transition() {
    return this.transition$.value;
  }
  private detectChangesEffect$ = merge(
    this.gridTemplateRows$,
    this.overflow$,
    this.transition$,
  ).pipe(
    takeUntil(this.destroyed$),
    rxNgZoneRun(this.ngZone),
    tap(() => this.cdr.markForCheck()),
  );

  constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.transition$.next(
        `${this.transitionDuration}ms ease grid-template-rows`,
      );
    }, 0);
    this.detectChangesEffect$.subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.clearTimeouts();
  }

  collapseTimeout: NodeJS.Timeout | null = null;
  unCollapseTimeout: NodeJS.Timeout | null = null;

  collapse(init = false): void {
    this.clearTimeouts();
    this.gridTemplateRows$.next('0fr');
    this.overflow$.next('hidden');

    // Set content as completely hidden after transition
    this.collapseTimeout = setTimeout(() => {
      this._contentCompletelyHidden$.next(true);
    }, this.transitionDuration);
  }

  unCollapse(init = false): void {
    this.clearTimeouts();
    this._contentCompletelyHidden$.next(false);
    this.overflow$.next('hidden');

    // Use setTimeout to ensure the grid-template-rows change happens after the content is rendered
    this.unCollapseTimeout = setTimeout(() => {
      this.gridTemplateRows$.next('1fr');
      this.unCollapseTimeout = setTimeout(() => {
        this.overflow$.next('visible');
      }, this.transitionDuration);
    }, 0);
  }

  private clearTimeouts(): void {
    this.unCollapseTimeout && clearTimeout(this.unCollapseTimeout);
    this.collapseTimeout && clearTimeout(this.collapseTimeout);
  }
}
